import { FC } from 'react';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import Autocomplete from '@material-ui/lab/Autocomplete';
import TextField from '@material-ui/core/TextField';
import CircularProgress from '@material-ui/core/CircularProgress';
import { useGithubBranches } from '../../hooks/useGithubBranches';
import { resolveOwnerRepo } from '../../services/githubService';

export const GithubBranchPicker: FC<FieldExtensionComponentProps<string>> = ({
  formData,
  onChange,
  formContext,
  uiSchema,
  schema,
  required,
}) => {
  // Repo can come from a watched form field (repoField) or a static value (repo)
  const repoFieldName = uiSchema?.['ui:options']?.repoField as string | undefined;
  const staticRepo = uiSchema?.['ui:options']?.repo as string | undefined;
  const formDataObj = formContext?.formData as Record<string, unknown> | undefined;
  const selectedRepo =
    staticRepo ??
    (repoFieldName ? (formDataObj?.[repoFieldName] as string | undefined) : undefined);

  // Support both "owner/repo" full names and bare repo names
  const ownerRepo = selectedRepo ? resolveOwnerRepo(selectedRepo) : undefined;

  const { branches, loading, error } = useGithubBranches(ownerRepo);

  const label = schema.title || 'Branch';
  const helperText =
    error ??
    (!ownerRepo ? 'Please select a repository first' : undefined) ??
    (uiSchema?.['ui:help'] as string | undefined) ??
    undefined;

  const selected = branches.find(b => b.name === formData) ?? null;

  return (
    <Autocomplete
      options={branches}
      getOptionLabel={option => option.name}
      value={selected}
      onChange={(_, newValue) => onChange(newValue?.name ?? '')}
      disabled={!ownerRepo || loading}
      loading={loading}
      renderInput={params => (
        <TextField
          {...params}
          label={label}
          variant="outlined"
          margin="normal"
          required={required}
          error={!!error}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};
